import {
    AppBar,
    Box,
    Checkbox,
    FormControlLabel,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
} from '@mui/material';
import {
    getDeviceAlertFirebase,
    getPortalUsersStatic,
    updateDeviceAlert,
} from 'lib/firebase';
import { useEffect, useState } from 'react';
import { StoredUser } from 'store/features/authentication';
import { Device } from 'types';
import styles from './DeviceAlerts.module.scss';
import {
    FormCheckbox,
    FormInputGroup,
    FormProvider,
    FormSubmitButton,
    FormText,
} from 'components/form';
import * as yup from 'yup';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { activateSnackbar } from 'store/features/ui';
import { deviceAlertTooltips } from 'lib/constants';
import { HelpOutline } from '@mui/icons-material';
import {
    // useLazyPatchUpdateDeviceAlertsQuery,
    useLazyPatchUpdateDeviceQuery,
} from 'store/services';
import { getObjectDiff } from 'helpers/general';
// import { getDeviceAlert } from 'lib/napi';
import { useSelector } from 'react-redux';
import { useViewAsSupplierSelector } from 'store/features/viewAsSupplier';
import Save from '@mui/icons-material/Save';

interface AlertsConfigProps {
    device: Device;
    user: StoredUser | null;
    setDevice: (d: Device) => void;
}

export interface AlertsConfig {
    enabled: boolean;
    trigger: number;
    cov_enabled: boolean;
    thresh_enabled: boolean;
    thresh1?: number;
    thresh2?: number;
    tcov?: number;
    users?: {
        [key: string]: string;
    };
    email?: string;
}

type AlertsUserObject = {
    [key: string]: string;
};

const alertsSchema = yup.object<{
    [k in keyof AlertsConfig]: yup.AnySchema;
}>({
    enabled: yup.boolean(),
    trigger: yup
        .number()
        .when('enabled', {
            is: true, // if enabled is true (checkbox is checked)
            then: (schema) => schema.min(15).max(100).required(), // then the trigger value is required
            otherwise: (schema) => schema.notRequired(),
        })
        .transform(
            (parsedValue, originalValue) =>
                originalValue === '' ? null : parsedValue // the number requirement was erroring when nothing was entered so this forces it to null
        )
        .nullable(),
    cov_enabled: yup.boolean(),
    thresh_enabled: yup.boolean(),
    thresh1: yup
        .number()
        .when('thresh_enabled', {
            is: true,
            then: (schema) => schema.min(15).max(100).required(),
            otherwise: (schema) => schema.notRequired(),
        })
        .transform((parsedValue, originalValue) =>
            originalValue === '' ? null : parsedValue
        )
        .nullable(),
    thresh2: yup
        .number()
        .when('thresh_enabled', {
            is: true,
            then: (schema) => schema.min(15).max(100).required(),
            otherwise: (schema) => schema.notRequired(),
        })
        .transform((parsedValue, originalValue) =>
            originalValue === '' ? null : parsedValue
        )
        .nullable(),
    tcov: yup
        .number()
        .transform((parsedValue, originalValue) =>
            originalValue === '' ? null : parsedValue
        )
        .nullable(),
});

export const DeviceAlertsConfig = ({
    device,
    user,
    setDevice,
}: AlertsConfigProps) => {
    const [alertConfigDefault, setAlertConfigDefault] = useState<AlertsConfig>({
        enabled: false,
        trigger: 0,
        cov_enabled: false,
        thresh_enabled: false,
        thresh1: 30,
        thresh2: 15,
        tcov: 20,
    });
    const [usersList, setUsersList] = useState<AlertsUserObject>({});
    const [availableUsers, setAvailableUsers] = useState<StoredUser[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [searchQuery, setSearchQuery] = useState('');

    const [enabledSubscribersOnly, setEnabledSubscribersOnly] = useState(false);

    const filteredSubscribers = availableUsers.filter((subscriber) => {
        if (enabledSubscribersOnly) {
            return (
                !!usersList?.[subscriber.uid as keyof typeof usersList] &&
                subscriber.email
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase())
            );
        }

        return subscriber.email
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase());
    });

    const dispatch = useDispatch();
    const { viewAsSupplier } = useSelector(useViewAsSupplierSelector);

    // const [updateDeviceAlertsQuery] = useLazyPatchUpdateDeviceAlertsQuery();
    const [updateDeviceQuery] = useLazyPatchUpdateDeviceQuery();

    // Assume availableUsers and other states are defined or passed as props
    const [orderDirection, setOrderDirection] = useState('desc');

    // Function to sort the users
    const handleSort = () => {
        const isAsc = orderDirection === 'asc';
        setAvailableUsers((prevUsers) => {
            return prevUsers.slice().sort((a, b) => {
                if (!a.email || !b.email) {
                    return 0;
                }
                return isAsc
                    ? a.email.localeCompare(b.email)
                    : b.email.localeCompare(a.email);
            });
        });
        setOrderDirection(isAsc ? 'desc' : 'asc');
    };

    useEffect(() => {
        if (
            device.deviceId &&
            (viewAsSupplier ||
                user?.protected?.supplierId?.toString() === '0') &&
            user?.idToken
        ) {
            Promise.all([
                // getDeviceAlert(
                //     device.deviceId,
                //     viewAsSupplier,
                //     user.idToken
                // ),
                getDeviceAlertFirebase(device.deviceId, viewAsSupplier),
                getPortalUsersStatic(viewAsSupplier),
            ])
                .then((result) => {
                    if (!result || !(result[0] || result[1])) {
                        return;
                    }

                    const newAlertConfig = result[0] ?? {};

                    if (!newAlertConfig.users) {
                        newAlertConfig.users = {};
                    }

                    const newUsersList: AlertsUserObject = {};

                    result[1].forEach((user) => {
                        if (!!newAlertConfig.users?.[user.uid]) {
                            newUsersList[user.uid] = user.email;
                        }
                    });

                    const { thresh1, thresh2, tcov } = device;

                    setAlertConfigDefault({
                        ...newAlertConfig,
                        thresh1,
                        thresh2,
                        tcov,
                    });
                    setUsersList(newUsersList);

                    const userList = result[1];
                    const sortedUserList = userList.sort((a, b) => {
                        return a.email.localeCompare(b.email);
                    });
                    setAvailableUsers(sortedUserList);
                })
                .catch((error) => {
                    dispatch(activateSnackbar(error));
                });
        }
    }, [
        setAlertConfigDefault,
        setUsersList,
        device,
        user,
        dispatch,
        viewAsSupplier,
    ]);

    const onSubmit = (data: FieldValues) => {
        // Update the default form values with the new data.
        setAlertConfigDefault((oldConfig) => ({
            ...oldConfig,
            ...data,
        }));

        setIsSaving(true);
        const newData = getObjectDiff(alertConfigDefault, data);

        // "Pop" data from the diff that only applies to the alert object (whether certain triggers are active).
        // For some reason, the actual thresholds etc are stored on the device and not the deviceAlerts object.
        // Except for trigger.
        // For some reason.
        const { thresh1, thresh2, tcov, ...newAlertData } = newData;

        const deviceData = {
            ...(thresh1 ? { thresh1 } : {}),
            ...(thresh2 ? { thresh2 } : {}),
            ...(tcov ? { tcov } : {}),
        };

        newAlertData.users = { ...usersList };
        newAlertData.email = Object.values(usersList).join(',');

        updateDeviceQuery({
            deviceId: device.deviceId,
            updatedFields: deviceData,
        })
            .then(({ data }) => {
                if (data) {
                    setDevice(data);
                }
                if (
                    viewAsSupplier ||
                    user?.protected?.supplierId?.toString() === '0'
                ) {
                    // 401s - not set up in NAPI yet
                    // updateDeviceAlertsQuery({
                    //     deviceId: device.deviceId,
                    //     supplierId: viewAsSupplier,
                    //     updatedFields: deviceAlertData,
                    // }).then(() => {
                    //     dispatch(
                    //         activateSnackbar({
                    //             message:
                    //                 'Alerts config updated. Changes take effect at next reading.',
                    //             severity: 'success',
                    //         })
                    //     );
                    // });

                    updateDeviceAlert(
                        device.deviceId,
                        viewAsSupplier,
                        newAlertData
                    ).then(() => {
                        dispatch(
                            activateSnackbar({
                                message:
                                    'Alerts config updated. Changes take effect at next reading.',
                                severity: 'success',
                            })
                        );
                    });
                }
            })
            .finally(() => setIsSaving(false));
    };

    const updateUsersList = (uid: string, email: string, checked: boolean) => {
        if (checked) {
            setUsersList((oldList) => ({ ...oldList, [uid]: email }));
        } else {
            setUsersList((list) => {
                const newUsersList = { ...list };
                delete newUsersList[uid];

                return newUsersList;
            });
        }
    };

    const isCellOrVirtual =
        device.connection_type === 'virtual' ||
        device.connection_type === 'cell';
    const isOnlyCell = device.connection_type === 'cell';

    return (
        <div data-testid="DeviceAlerts" className={styles.container}>
            <FormProvider
                defaultValues={alertConfigDefault}
                validationSchema={alertsSchema}
            >
                <ConfigForm
                    isCellOrVirtual={isCellOrVirtual}
                    isOnlyCell={isOnlyCell}
                />
                <Paper className={styles.configBox}>
                    <h1>Alert Subscribers</h1>
                    <small>
                        Select all emails you would like to receive alerts
                    </small>
                    <TableContainer className={styles.usersList}>
                        <AppBar
                            position="sticky"
                            color="default"
                            elevation={1}
                            sx={{
                                backgroundColor: '#fff', // or any color you prefer
                                boxShadow: 'none',
                            }}
                        >
                            <Toolbar variant="dense">
                                <Box
                                    id="enabledSubscribersOnlyFilter"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%" // Make sure it occupies the full width
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={enabledSubscribersOnly}
                                                onChange={(e) =>
                                                    setEnabledSubscribersOnly(
                                                        e.target.checked
                                                    )
                                                }
                                                name="enabledSubscribersOnly"
                                                color="primary"
                                            />
                                        }
                                        label="Show Enabled Subscribers Only"
                                    />
                                </Box>
                            </Toolbar>
                        </AppBar>

                        <Table stickyHeader>
                            <TableHead
                                sx={{
                                    top: '48px',
                                    position: 'sticky',
                                    backgroundColor: 'white', // or any color that matches your design
                                    zIndex: 2,
                                }}
                            >
                                <TableRow>
                                    <TableCell style={{ width: '50px' }}>
                                        Enabled
                                    </TableCell>
                                    <TableCell
                                        onClick={handleSort}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="flex-start"
                                        >
                                            <span>
                                                Subscribers{' '}
                                                {orderDirection === 'asc'
                                                    ? '↑'
                                                    : '↓'}
                                            </span>
                                            <TextField
                                                label="Search"
                                                variant="outlined"
                                                size="small"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                value={searchQuery}
                                                onChange={(e) => {
                                                    setSearchQuery(
                                                        e.target.value
                                                    );
                                                }}
                                                sx={{ ml: 2, width: '30%' }} // Adjust margins and width as needed
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSubscribers.map((user) => (
                                    <TableRow key={user?.uid}>
                                        <TableCell style={{ width: '50px' }}>
                                            <Checkbox
                                                checked={
                                                    !!usersList?.[
                                                        user.uid as keyof typeof usersList
                                                    ]
                                                }
                                                onChange={(e) => {
                                                    if (
                                                        user.uid &&
                                                        user.email
                                                    ) {
                                                        updateUsersList(
                                                            user?.uid,
                                                            user?.email,
                                                            e.target.checked
                                                        );
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <div className={styles.submitContainer}>
                    <FormSubmitButton
                        onValidHandler={onSubmit}
                        onErrorHandler={(data) => data}
                        className={styles.submitButton}
                        isLoading={isSaving}
                        startIcon={<Save />}
                        buttonLabel="Save"
                    />
                </div>
            </FormProvider>
        </div>
    );
};

interface ConfigFormProps {
    isCellOrVirtual: boolean;
    isOnlyCell: boolean;
}

const ConfigForm = ({ isCellOrVirtual, isOnlyCell }: ConfigFormProps) => {
    const { watch } = useFormContext();

    const isThreshEnabled = watch('thresh_enabled');
    const isClassicEnabled = watch('enabled');

    return (
        <Paper className={styles.configBox}>
            <h1>Alerts Config</h1>
            {isCellOrVirtual && (
                <div className={styles.threshConfig}>
                    <FormInputGroup>
                        <FormCheckbox
                            field="thresh_enabled"
                            label="Threshold Alerts"
                        />
                    </FormInputGroup>
                    <FormInputGroup>
                        <div className={styles.threshInput}>
                            <FormText
                                field="thresh1"
                                isNumber
                                label={`Warning Threshold${
                                    isThreshEnabled ? ' *' : ''
                                }`}
                                disabled={!isThreshEnabled}
                            />
                            <Tooltip
                                title={deviceAlertTooltips['warningThreshold']}
                                enterTouchDelay={0}
                                placement="bottom"
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                        <div className={styles.threshInput}>
                            <FormText
                                field="thresh2"
                                isNumber
                                label="Critical Threshold"
                                disabled={!isThreshEnabled}
                            />
                            <Tooltip
                                title={deviceAlertTooltips['warningThreshold']}
                                enterTouchDelay={0}
                                placement="bottom"
                            >
                                <HelpOutline color="action" fontSize="small" />
                            </Tooltip>
                        </div>
                    </FormInputGroup>
                    {isOnlyCell && (
                        <div className={styles.threshConfig}>
                            <FormInputGroup>
                                <div className={styles.threshInput}>
                                    <FormText
                                        field="tcov"
                                        label="Change of Tank Level Since Last Transmission"
                                        isNumber
                                        disabled={!isThreshEnabled}
                                    />
                                    <Tooltip
                                        title={
                                            deviceAlertTooltips['changeOfValue']
                                        }
                                        enterTouchDelay={0}
                                        placement="bottom"
                                    >
                                        <HelpOutline
                                            color="action"
                                            fontSize="small"
                                        />
                                    </Tooltip>
                                </div>
                            </FormInputGroup>
                        </div>
                    )}
                </div>
            )}
            <FormInputGroup>
                <FormCheckbox field="enabled" label="Classic Alerts" />
            </FormInputGroup>
            <FormInputGroup>
                <div className={styles.threshInput}>
                    <FormText
                        field="trigger"
                        isNumber
                        className={styles.createTicketInput}
                        label={`Classic Alert${isClassicEnabled ? ' *' : ''}`}
                        disabled={!isClassicEnabled}
                    />
                    <Tooltip
                        title={deviceAlertTooltips['classicAlert']}
                        enterTouchDelay={0}
                        placement="bottom"
                    >
                        <HelpOutline color="action" fontSize="small" />
                    </Tooltip>
                </div>
            </FormInputGroup>
        </Paper>
    );
};
